// GtRolesTable — the Roles centre screen: schema-driven table + filter rail + saved
// views + column chooser + CSV export. Mirrors GtCandidatesTable (built 22 Jul), server
// -driven through /api/filter/roles. Candidate-only pieces (tags, CV upload) dropped;
// "New role" with JD-paste-to-extract is the primary action instead. Single-tenant.
import { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from '../../config';
import GtTable from '../../components/GtTable';
import { SearchHint, splitSearch, searchCondition, searchSort } from '../../components/gtSearch'; // C1-SEARCH
import GtPager from '../../components/GtPager';
import { GtSelect } from '../../components/GtControls';
import { ClientField, ExpertiseField } from '../../components/GtTaxonomyFields';
import GtEditor from '../../components/GtEditor';
import { ROLE_COLUMNS, ALL_ROLE_COLUMNS } from '../../components/gtRoleColumns';
import GtFilterRail from '../../components/GtFilterRail';
import { ROLE_SCHEMA } from '../../components/gtRoleFilterSchema';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import GtRoleRecord from './GtRoleRecord';
import { paths } from '../../components/gtRoutes';

// Expertise domains come from the database, never a hardcoded copy: they are
// foreign-keyed from candidates and roles, so a list that drifts from the table
// produces values the API rejects.

const PLACEMENT_OPTIONS = [
  { value: 'permanent_fee', label: 'Permanent (fee)' },
  { value: 'contract_rate_card', label: 'Contract (rate card)' },
];

export default function GtRolesTable({ getToken, userName, userImage }) {
  // The open record is the URL, not local state, so refresh lands where you were.
  const { roleId, candidateId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = roleId || null;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchMode, setSearchMode] = useState('auto'); // C1-SEARCH
  const [searchInfo, setSearchInfo] = useState(null);
  const routeLoc = useLocation();
  // ?q= from search everywhere ("See all ... in the list") fills the box once.
  useEffect(() => {
    const sp = new URLSearchParams(routeLoc.search);
    const v = sp.get('q');
    if (v == null) return;
    setQuery(v); setDebouncedQuery(v.trim()); setSearchMode('auto');
    sp.delete('q');
    navigate({ pathname: routeLoc.pathname, search: sp.toString() ? '?' + sp.toString() : '' }, { replace: true });
  }, [routeLoc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // A saved view can hold a search: it goes back into the box, the rest into the rail.
  function applyViewTree(t) {
    const s = splitSearch(t);
    setFilterTree(s.tree); setQuery(s.q); setDebouncedQuery(s.q.trim()); setSearchMode(s.mode);
  }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(() => ROLE_COLUMNS.map(c => c.key));
  const [draftKeys, setDraftKeys] = useState(() => ROLE_COLUMNS.map(c => c.key));
  const [dragKey, setDragKey] = useState(null);

  const [filterTree, setFilterTree] = useState(null);
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [distincts, setDistincts] = useState({});

  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ title: '', client_id: '', expertise_domain: 'engineering', placement_model: 'permanent_fee', jd_html: '', interview_mode: 'manual' });


  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(body.detail || body.error || ('request failed: ' + res.status));
      err.status = res.status; err.body = body;
      throw err;
    }
    return body;
  }, [getToken]);

  useEffect(() => { if (showColumns) setDraftKeys(visibleKeys); }, [showColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(() => setDebouncedQuery(query.trim()), 250); return () => clearTimeout(t); }, [query]);

  const filterActive = !!(filterTree && Array.isArray(filterTree.and) && filterTree.and.length > 0);

  const effectiveTree = useCallback(() => {
    const parts = [];
    if (filterTree && Array.isArray(filterTree.and)) parts.push(...filterTree.and);
    const sc = searchCondition(debouncedQuery, searchMode); // C1-SEARCH: the whole record, typos, Boolean
    if (sc) parts.push(sc);
    return parts.length ? { and: parts } : null;
  }, [filterTree, debouncedQuery, searchMode]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch('/api/filter/roles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tree: effectiveTree(), sort: searchSort(sort, debouncedQuery), limit: pageSize, offset: page * pageSize }),
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSearchInfo(data.search || null);
    } catch (e) { setError(e.message); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [authFetch, effectiveTree, sort, page, pageSize, debouncedQuery]);
  useEffect(() => { load(); }, [load]);
  // See GtCandidatesTable: a ?preset= from Home seeds the filter once.
  useEffect(() => {
    const preset = searchParams.get('preset');
    if (!preset) return;
    const trees = {
      unowned: { and: [{ field: 'has_owner', op: 'is_null' }] },
    };
    if (trees[preset]) {
      setFilterTree(trees[preset]);
      setShowFilters(true);
    }
    searchParams.delete('preset');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !openId) load();
    wasOpen.current = !!openId;
  }, [openId, load]);
  useEffect(() => { setPage(0); }, [debouncedQuery, pageSize, filterTree]);

  const loadSavedViews = useCallback(async () => {
    try { setSavedViews(await authFetch('/api/saved-filters/roles')); } catch (e) { /* non-fatal */ }
  }, [authFetch]);
  const loadDistincts = useCallback(async () => {
    const keys = [...new Set((ROLE_SCHEMA.fields || []).filter(f => f.dropdown).map(f => f.dropdown))];
    const out = {};
    await Promise.all(keys.map(async k => {
      try { out[k] = await authFetch('/api/filter/roles/distinct/' + encodeURIComponent(k)); } catch (e) { /* skip */ }
    }));
    setDistincts(out);
  }, [authFetch]);
  useEffect(() => { loadSavedViews(); loadDistincts(); }, [loadSavedViews, loadDistincts]);

  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }

  async function saveCurrentView(name, shared) {
    try {
      await authFetch('/api/saved-filters/roles', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, tree: effectiveTree() || filterTree, shared: !!shared }) });
      loadSavedViews();
    } catch (e) { flash(e.message); }
  }
  async function deleteView(viewId) {
    try {
      await authFetch('/api/saved-filters/' + viewId, { method: 'DELETE' });
      if (activeViewId === viewId) { setActiveViewId(null); setFilterTree(null); }
      loadSavedViews();
    } catch (e) { flash(e.message); }
  }



  async function createRole() {
    if (!nf.title.trim() || !nf.client_id) { flash('Title and client are required'); return; }
    setCreating(true);
    try {
      const created = await authFetch('/api/roles', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...nf, client_id: Number(nf.client_id) }),
      });
      // If a JD was pasted, kick off extraction immediately so the profile is ready.
      if (nf.jd_html && nf.jd_html.trim()) {
        try { await authFetch(`/api/roles/${created.id}/extract-profile`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }); }
        catch (e) { flash('Role created, but profile extraction failed: ' + e.message); }
        // With an interview on, Nathan's plan is written from the profile straight away
        // (in the background), ready to read and approve on the Interview tab.
        if (nf.interview_mode !== 'off') {
          authFetch(`/api/interview-plans/role/${created.id}/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => {});
        }
      }
      setShowNew(false);
      setNf({ title: '', client_id: '', expertise_domain: 'engineering', placement_model: 'permanent_fee', jd_html: '', interview_mode: 'manual' });
      await load();
      navigate(paths.role(created.ref || created.id));
    } catch (e) { flash(e.message); }
    finally { setCreating(false); }
  }

  const toggleRow = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelectedIds(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));

  function onSort(field) {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }

  function applyColumns() { setVisibleKeys(draftKeys); setShowColumns(false); }
  const columns = visibleKeys.map(k => ALL_ROLE_COLUMNS.find(c => c.key === k)).filter(Boolean);

  const exportCsv = () => {
    const cols = columns;
    const header = cols.map(c => c.label).join(',');
    const lines = rows.map(r => cols.map(c => {
      const v = r[c.key];
      const s = v == null ? '' : String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(','));
    const csv = [header, ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `roles-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (openId) return <GtRoleRecord roleId={openId} initialCandidateId={candidateId || null} getToken={getToken} userName={userName} userImage={userImage}
    onBack={() => navigate(paths.roles())}
    onOpenCandidate={(cid) => navigate(paths.roleCandidate(openId, cid))}
    onCloseCandidate={() => navigate(paths.role(openId))} />;

  return (
    <div className="gtc-wrap">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Roles</h1>
          <p className="gtc-sub">{total} role{total === 1 ? '' : 's'}{filterActive || debouncedQuery ? ' matching' : ''}</p>
        </div>
        <div className="gtc-head-right">
          <button className="gtc-primary" type="button" onClick={() => setShowNew(true)}>New role</button>
        </div>
      </div>

      <div className="gtc-tabs">
        <button type="button" className={'gtc-tab' + (activeViewId === null ? ' gtc-tab--on' : '')}
          onClick={() => { setActiveViewId(null); setFilterTree(null); setPage(0); }}>All roles</button>
        {savedViews.map(v => (
          <button key={v.id} type="button" className={'gtc-tab' + (activeViewId === v.id ? ' gtc-tab--on' : '')}
            onClick={() => { setActiveViewId(v.id); applyViewTree(v.tree); setPage(0); }}>{v.name}</button>
        ))}
        <button type="button" className="gtc-tab gtc-tab--add" title="New view" onClick={() => setShowFilters(true)}>+</button>
      </div>

      <div className="gtc-toolbar">
        <div className="gtc-tb-search">
          <span className="gtc-tb-searchicon">⌕</span>
          <input placeholder="Search roles: title, client, job description, notes…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="gtc-tb-actions">
          <button className={'gtc-tb-btn' + (showColumns ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setShowColumns(v => !v)}><span className="gtc-tb-ic">▦</span>Columns</button>
          <button className={'gtc-tb-btn' + (filterActive ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setShowFilters(v => !v)}>
            <span className="gtc-tb-ic">⛃</span>Filters{filterActive ? ` · ${filterTree.and.length}` : ''}
          </button>
          <button className="gtc-tb-btn" type="button" title="Export current view to CSV" onClick={exportCsv}><span className="gtc-tb-ic">↧</span>Export</button>
        </div>
      </div>

      <SearchHint info={searchInfo} query={debouncedQuery} mode={searchMode} onMode={setSearchMode}
        onQuery={(t) => { setQuery(t); setDebouncedQuery(t); setSearchMode('auto'); }} /> {/* C1-AISEARCH */}

      {showColumns && (
        <div className="gtc-colpanel">
          <div className="gtc-colpanel-head">
            <span>Edit columns</span>
            <button type="button" className="gtc-colpanel-reset" onClick={() => setDraftKeys(ROLE_COLUMNS.map(c => c.key))}>Reset to default</button>
          </div>
          <div className="gtc-colpanel-cols">
            <div className="gtc-colpanel-visible">
              <div className="gtc-colpanel-subhead">Visible — drag to reorder</div>
              {draftKeys.map(k => {
                const col = ALL_ROLE_COLUMNS.find(c => c.key === k);
                if (!col) return null;
                const locked = col.key === 'id' || col.key === 'title';
                return (
                  <div key={col.key}
                    className={'gtc-colrow' + (locked ? ' gtc-colrow--locked' : '') + (dragKey === col.key ? ' gtc-colrow--dragging' : '')}
                    draggable={!locked}
                    onDragStart={() => !locked && setDragKey(col.key)}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={e => {
                      e.preventDefault();
                      if (locked || !dragKey || dragKey === col.key) return;
                      setDraftKeys(prev => {
                        const next = prev.filter(x => x !== dragKey);
                        const idx = next.indexOf(col.key);
                        next.splice(idx, 0, dragKey);
                        return next;
                      });
                    }}>
                    <span className="gtc-colgrip">{locked ? '🔒' : '⠿'}</span>
                    <span className="gtc-collabel">{col.label}</span>
                    {!locked && (
                      <button type="button" className="gtc-colhide" onClick={() => setDraftKeys(prev => prev.filter(x => x !== col.key))}>Hide</button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="gtc-colpanel-hidden">
              <div className="gtc-colpanel-subhead">Hidden — click to add</div>
              {ALL_ROLE_COLUMNS.filter(c => !draftKeys.includes(c.key)).map(col => (
                <button key={col.key} type="button" className="gtc-coladd" onClick={() => setDraftKeys(prev => [...prev, col.key])}>+ {col.label}</button>
              ))}
            </div>
          </div>
          <div className="gtc-colpanel-foot">
            <button type="button" className="gtc-btn-ghost" onClick={() => setShowColumns(false)}>Cancel</button>
            <button type="button" className="gtc-primary" onClick={applyColumns}>Apply</button>
          </div>
        </div>
      )}

      <div className="gtc-body">
        {showFilters && (
          <GtFilterRail
            schema={ROLE_SCHEMA}
            value={filterTree}
            onChange={setFilterTree}
            matchCount={total}
            loading={loading}
            savedViews={savedViews}
            onSaveView={saveCurrentView}
            onApplyView={(v) => { setActiveViewId(v.id); applyViewTree(v.tree); }}
            onDeleteView={deleteView}
            distincts={distincts}
          />
        )}
        <div className="gtc-main">
          {error && <div className="gtc-error">{error}</div>}
          <GtTable
            columns={columns}
            rows={rows}
            loading={loading}
            selectedIds={selectedIds}
            onToggle={toggleRow}
            onToggleAll={toggleAll}
            onCandidateClick={(row) => navigate(paths.role(row.ref || row.id))}
            sort={sort}
            onSort={onSort}
          />
          <GtPager page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />
        </div>
      </div>

      {showNew && (
        <div className="gtc-modal-backdrop" onClick={() => !creating && setShowNew(false)}>
          <div className="gtc-modal" onClick={e => e.stopPropagation()}>
            <div className="gtc-modal-head">New role</div>
            <div className="gtc-modal-body">
              <label className="gtc-field">
                <span>Title</span>
                <input value={nf.title} onChange={e => setNf({ ...nf, title: e.target.value })} placeholder="Senior Go Engineer" />
              </label>
              <label className="gtc-field">
                <span>Client</span>
                <ClientField value={nf.client_id} onChange={v => setNf({ ...nf, client_id: v })} authFetch={authFetch} flash={flash} />
              </label>
              <label className="gtc-field">
                <span>Expertise domain</span>
                <ExpertiseField value={nf.expertise_domain} onChange={v => setNf({ ...nf, expertise_domain: v })} authFetch={authFetch} flash={flash} />
              </label>
              <label className="gtc-field">
                <span>Placement model</span>
                <GtSelect value={nf.placement_model} options={PLACEMENT_OPTIONS} onChange={v => setNf({ ...nf, placement_model: v })} />
              </label>
              <label className="gtc-field">
                <span>Interview with Nathan (AI interviewer)</span>
                <GtSelect value={nf.interview_mode} onChange={v => setNf({ ...nf, interview_mode: v })} options={[
                  { value: 'manual', label: 'Invite by hand' },
                  { value: 'on_apply', label: 'Part of applying (after the plan is approved)' },
                  { value: 'off', label: 'Off' },
                ]} />
              </label>
              <label className="gtc-field">
                <span>Job description (optional — paste to auto-extract a Role Profile)</span>
                <GtEditor value={nf.jd_html} onChange={v => setNf({ ...nf, jd_html: v })} placeholder="Paste the JD here. On save, structured requirements are extracted for you to review." minHeight={200} />
              </label>
            </div>
            <div className="gtc-modal-foot">
              <button type="button" className="gtc-btn-ghost" disabled={creating} onClick={() => setShowNew(false)}>Cancel</button>
              <button type="button" className="gtc-primary" disabled={creating} onClick={createRole}>
                {creating ? (nf.jd_html.trim() ? 'Creating and extracting' : 'Creating') : 'Create role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="gtc-toast">{toast}</div>}
    </div>
  );
}
